import { AnalyticsProvider as LibraryAnalyticsProvider, useAnalytics } from '@sixfold/app-analytics-framework';
import * as React from 'react';

import { useCurrentUser } from '../../lib/authorization';
import { getEmbedConfig } from '../../lib/data';

// Import the CurrentUser type from authorization
type CurrentUser = {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  backoffice_role: string | null;
  backoffice_scopes: string[] | null;
};

type Props = {
  user: CurrentUser | null;
};

export const IntercomPropsReporter: React.FunctionComponent<Props> = ({ user }) => {
  const intercom = useAnalytics();

  React.useEffect(() => {
    if (!intercom || !user) {
      return;
    }

    try {
      intercom.setIntercomProperties({
        email: user.email ?? undefined,
        name: [user.first_name, user.last_name].filter(Boolean).join(' '),
      });
    } catch (error) {
      console.error('Failed to set Intercom properties:', error);
    }
  }, [intercom, user]);

  // We don't render any DOM
  return null;
};

/**
 * Higher-order component that wraps content with IntercomPropsReporter.
 * @param user - The current user object
 * @param node - The React node to wrap
 * @returns A React fragment containing the original node and IntercomPropsReporter
 */
export const withIntercomProps = (user: CurrentUser | null, node: React.ReactNode) => (
  <>
    {node}
    <IntercomPropsReporter user={user} />
  </>
);

/**
 * Provider component that wraps the application with Intercom analytics.
 * Only renders the analytics provider if Intercom is properly configured.
 */
export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const currentUser = useCurrentUser();
  const config = getEmbedConfig();
  const userId = currentUser?.user_id;

  const intercomOptions: React.ComponentProps<typeof LibraryAnalyticsProvider>['intercomOptions'] =
    React.useMemo(() => {
      if (!config?.intercom_appid) {
        return undefined;
      }

      return {
        appId: config.intercom_appid,
        shouldTrackCustomEvents: false,
        hideDefaultLauncher: !config.intercom_chatbot_enabled || userId === null,
      };
    }, [config?.intercom_appid, config?.intercom_chatbot_enabled, userId]);

  // Don't render the provider if no app ID is configured
  if (!config?.intercom_appid) {
    return <>{children}</>;
  }

  return (
    <LibraryAnalyticsProvider userId={userId} intercomOptions={intercomOptions} routes={[]} isTrackingAllowed={true}>
      {children}
    </LibraryAnalyticsProvider>
  );
};
